import { AxiosInstance, AxiosResponse } from 'axios'
import { idItem } from '../server.api'
import { SanteOptionCreationMode } from './santeOption.module.api'

// Api interface
export default class SanteAccordApi {
  instance: AxiosInstance

  constructor(instance: AxiosInstance) {
    this.instance = instance
  }

  async getCcnAccords(ccnId: number): Promise<AxiosResponse<SanteAccordSummary[]>> {
    return this.instance.get(`/api/v2/sante/ccns/${ccnId}/accords`)
  }

  async getCcnAccordsView(
    ccnId: number,
    accordId?: number,
    optionIds?: number[]
  ): Promise<AxiosResponse<CcnAccordsView>> {
    return this.instance.get(`/api/v2/sante/ccns/${ccnId}/accords/view`, {
      params: { accordId, optionIds },
      paramsSerializer: serializeSanteQuery,
    })
  }

  async createAccord(input: SanteAccordCreate): Promise<AxiosResponse<idItem>> {
    return this.instance.post('/api/v2/sante/accords', input)
  }

  async copyAccord(sourceAccordId: number, input: SanteAccordCopy): Promise<AxiosResponse<idItem>> {
    return this.instance.post(`/api/v2/sante/accords/${sourceAccordId}/copy`, input)
  }

  async updateAccord(accordId: number, input: SanteAccordPatch): Promise<AxiosResponse<idItem>> {
    return this.instance.patch(`/api/v2/sante/accords/${accordId}`, input)
  }

  async deleteAccord(accordId: number): Promise<AxiosResponse<idItem>> {
    return this.instance.delete(`/api/v2/sante/accords/${accordId}`)
  }

  async saveGarantie(
    optionId: number,
    prestationId: number,
    input: SanteGarantieSave
  ): Promise<AxiosResponse<SanteGarantieView>> {
    return this.instance.put(`/api/v2/sante/options/${optionId}/garanties/${prestationId}`, input)
  }

  async deleteGarantie(optionId: number, prestationId: number): Promise<AxiosResponse<idItem>> {
    return this.instance.delete(`/api/v2/sante/options/${optionId}/garanties/${prestationId}`)
  }

  async savePrestationGaranties(
    accordId: number,
    prestationId: number,
    input: SanteGarantieRowSave
  ): Promise<AxiosResponse<SanteGarantieView[]>> {
    return this.instance.put(`/api/v2/sante/accords/${accordId}/prestations/${prestationId}/garanties`, input)
  }


  async getVersioning(accordId: number): Promise<AxiosResponse<SanteAccordVersioningView>> {
    return this.instance.get(`/api/v2/sante/accords/${accordId}/versioning`)
  }

  async getVersion(
    accordId: number,
    versionId: number,
    pagination?: SanteChangesPagination
  ): Promise<AxiosResponse<SanteAccordVersionDetail>> {
    return this.instance.get(`/api/v2/sante/accords/${accordId}/versions/${versionId}`, {
      params: pagination
        ? { changesPage: pagination.page, changesPageSize: pagination.pageSize }
        : undefined,
    })
  }

  async createVersion(accordId: number, input: SanteAccordVersionCreate): Promise<AxiosResponse<idItem>> {
    return this.instance.post(`/api/v2/sante/accords/${accordId}/versions`, input)
  }

  async updateVersion(
    accordId: number,
    versionId: number,
    input: SanteAccordVersionUpdate
  ): Promise<AxiosResponse<idItem>> {
    return this.instance.patch(`/api/v2/sante/accords/${accordId}/versions/${versionId}`, input)
  }

  async updateVersionGarantie(
    accordId: number,
    versionId: number,
    optionNumero: number,
    prestationId: number,
    input: SanteAccordVersionGarantieUpdate
  ): Promise<AxiosResponse<SanteAccordVersionGarantieView>> {
    return this.instance.patch(
      `/api/v2/sante/accords/${accordId}/versions/${versionId}/options/${optionNumero}/garanties/${prestationId}`,
      input
    )
  }

  async saveVersionPrestationGaranties(
    accordId: number,
    versionId: number,
    prestationId: number,
    input: SanteAccordVersionGarantieRowSave
  ): Promise<AxiosResponse<SanteAccordVersionGarantieView[]>> {
    return this.instance.put(
      `/api/v2/sante/accords/${accordId}/versions/${versionId}/prestations/${prestationId}/garanties`,
      input
    )
  }

  async createVersionOption(
    accordId: number,
    versionId: number,
    input: SanteAccordVersionOptionCreate
  ): Promise<AxiosResponse<SanteAccordVersionOptionMutationResult>> {
    return this.instance.post(`/api/v2/sante/accords/${accordId}/versions/${versionId}/options`, input)
  }

  async updateVersionOption(
    accordId: number,
    versionId: number,
    optionNumero: number,
    input: SanteAccordVersionOptionUpdate
  ): Promise<AxiosResponse<SanteAccordVersionOptionMutationResult>> {
    return this.instance.put(
      `/api/v2/sante/accords/${accordId}/versions/${versionId}/options/${optionNumero}`,
      input
    )
  }

  async deleteVersionOption(
    accordId: number,
    versionId: number,
    optionNumero: number
  ): Promise<AxiosResponse<SanteAccordVersionOptionMutationResult>> {
    return this.instance.delete(`/api/v2/sante/accords/${accordId}/versions/${versionId}/options/${optionNumero}`)
  }

  async reassignVersionChanges(
    accordId: number,
    input: SanteAccordVersionChangeReassignment
  ): Promise<AxiosResponse<SanteAccordVersionChangeReassignmentResult>> {
    return this.instance.patch(`/api/v2/sante/accords/${accordId}/version-changes/assignment`, input)
  }

}

const serializeSanteQuery = (params: { accordId?: number; optionIds?: number[] }): string => {
  const query = new URLSearchParams()

  if (params.accordId !== undefined) query.append('accordId', String(params.accordId))
  params.optionIds?.forEach((optionId) => query.append('optionIds', String(optionId)))

  return query.toString()
}

const SANTE_BASE_OPTION_NUMERO = 1
const isSanteBaseOption = (option: Pick<SanteOptionSummary, 'numero'>): boolean =>
  option.numero === SANTE_BASE_OPTION_NUMERO

type SanteAccordIncludeRss = 'ForceNoInclude' | 'ForceInclude' | null

type SanteAccordSummary = {
  id: number
  nomenclatureId: number
  nomenclatureName: string
  ccnId: number
  cibleId: number
  cibleName: string
  displayOrder: number
  options: SanteOptionSummary[]
}

type SanteOptionSummary = {
  id: number
  numero: number
  name: string
  isBaseComplement: boolean
  includesSecuriteSociale: boolean | null
}

type SanteGarantieView = {
  id: number
  optionId: number
  optionNumero: number
  optionName: string
  prestationId: number
  valeur: string
  valeurUniteId: number
  valeurUniteName: string
  commentaire: string | null
  isActive: boolean
  forceRss: SanteAccordIncludeRss
}

type SantePrestationStructure = {
  id: number
  name: string
  order: number
  hide: boolean | null
  refundRate: number | null
  refundAmount: number | null
  refundMinimum: number | null
  refundMaximum: number | null
  excludeComparison: boolean
  excludeSocialSecurityRefund: boolean
}

type SanteCategoryStructure = {
  id: number
  parentId: number | null
  name: string
  order: number
  depth: number
  commentaire: string | null
  categories: SanteCategoryStructure[]
  prestations: SantePrestationStructure[]
}

type SanteNomenclatureStructure = {
  id: number
  name: string
  categories: SanteCategoryStructure[]
}

type CcnAccordsView = {
  ccnId: number
  ccnCode: string
  ccnName: string
  accords: SanteAccordSummary[]
  selectedAccord: SanteAccordSummary
  nomenclature: SanteNomenclatureStructure
  garanties: SanteGarantieView[]
}

type SanteAccordCreate = {
  ccnId: number
  cibleId: number
  nomenclatureId: number
}

type SanteAccordCopy = {
  ccnId: number
  cibleId: number
}

type SanteAccordPatch = {
  cibleId?: number | null
  displayOrder?: number | null
}


type SanteChangesPagination = {
  page: number
  pageSize: number
}

type SanteAccordVersionEffectiveStatus = 'NotEffective' | 'Effective'

type SanteAccordVersionExtensionStatus = 'NotExtended' | 'Extended'

type SanteAccordVersionSourceType = 'TripalioArticle' | 'PdfOrTripalioLibrary' | 'Other'

type SanteAccordVersionEntityType = 'Accord' | 'Option' | 'Garantie'

type SanteAccordVersionChangeType = 'Added' | 'Modified' | 'Removed'

type SanteAccordVersionSummary = {
  id: number
  versionNumber: number
  isClientVisible: boolean
  isClientPublished: boolean
  isLatestClientVisible: boolean
  isCurrentEffectiveVersion: boolean
  effectiveStatus: SanteAccordVersionEffectiveStatus
  extensionStatus: SanteAccordVersionExtensionStatus
  versionReference: string | null
  signatureDate: string | null
  effectiveDate: string | null
  extensionDate: string | null
  sourceType: SanteAccordVersionSourceType | null
  sourceReference: string | null
  changesCount: number
  createdAt: string
  updatedAt: string
}

type SanteAccordVersionChangeView = {
  id: number | null
  versionId: number | null
  entityType: SanteAccordVersionEntityType
  changeType: SanteAccordVersionChangeType
  field: string | null
  sourceOptionId: number | null
  optionNumero: number | null
  optionName: string | null
  sourceGarantieId: number | null
  prestationId: number | null
  prestationName: string | null
  beforeJson: string | null
  afterJson: string | null
  previousValue: string | null
  newValue: string | null
  createdAt: string | null
  updatedAt: string | null
}

type SanteAccordVersionChangeReassignment = {
  changeIds: number[]
  versionId: number
}

type SanteAccordVersionChangeReassignmentResult = {
  accordId: number
  versionId: number
  updatedChanges: number
  updatedAt: string
}

type SanteAccordVersioningView = {
  accordId: number
  needsInitialVersion: boolean
  versions: SanteAccordVersionSummary[]
  comparisonError: string | null
}

type SanteAccordVersionGarantieView = {
  sourceGarantieId: number | null
  prestationId: number
  prestationName: string
  prestationOrder: number
  categoryId: number
  categoryName: string
  categoryOrder: number
  categoryPath: string
  valeur: string
  valeurUniteId: number
  valeurUniteName: string
  commentaire: string | null
  isActive: boolean
  forceRss: SanteAccordIncludeRss
}

type SanteAccordVersionOptionView = {
  sourceOptionId: number | null
  numero: number
  name: string
  isBaseComplement: boolean
  includesSecuriteSociale: boolean | null
  garanties: SanteAccordVersionGarantieView[]
}

type SanteAccordVersionAffectedGarantieView = {
  sourceOptionId: number | null
  optionNumero: number
  optionName: string
  garantie: SanteAccordVersionGarantieView
}

type SanteAccordVersionDetail = {
  accordId: number
  versionId: number
  versionNumber: number
  isClientVisible: boolean
  isClientPublished: boolean
  effectiveStatus: SanteAccordVersionEffectiveStatus
  extensionStatus: SanteAccordVersionExtensionStatus
  versionReference: string | null
  signatureDate: string | null
  effectiveDate: string | null
  extensionDate: string | null
  sourceType: SanteAccordVersionSourceType | null
  sourceReference: string | null
  createdAt: string
  updatedAt: string
  nomenclatureId: number
  nomenclatureName: string
  ccnId: number
  ccnCode: string
  ccnName: string
  cibleId: number
  cibleName: string
  options: SanteAccordVersionOptionView[]
  affectedGaranties: SanteAccordVersionAffectedGarantieView[]
  changes: SanteAccordVersionChangeView[]
  changesTotalCount: number
}

type SanteAccordVersionCreate = {
  isClientPublished: boolean
  versionReference: string | null
  signatureDate: string | null
  effectiveDate: string | null
  extensionDate: string | null
  effectiveStatus: SanteAccordVersionEffectiveStatus
  extensionStatus: SanteAccordVersionExtensionStatus
  sourceType: SanteAccordVersionSourceType | null
  sourceReference: string | null
}

type SanteAccordVersionUpdate = SanteAccordVersionCreate

type SanteAccordVersionOptionCreate = {
  name: string
  numero: number
  isBaseComplement: boolean
  includesSecuriteSociale: boolean | null
  creationMode: SanteOptionCreationMode
  sourceOptionNumero: number | null
}

type SanteAccordVersionOptionUpdate = {
  name: string
  numero: number
  isBaseComplement: boolean
  includesSecuriteSociale: boolean | null
}

type SanteAccordVersionOptionMutationResult = {
  versionId: number
  sourceOptionId: number | null
  optionNumero: number
}

type SanteAccordVersionGarantieUpdate = {
  valeur: string | null
  valeurUniteId: number
  commentaire: string | null
  isActive: boolean
  forceRss: SanteAccordIncludeRss
}

type SanteAccordVersionGarantieRowSaveItem = {
  optionNumero: number
  present: boolean
  valeur: string | null
  valeurUniteId: number | null
  commentaire: string | null
  isActive: boolean | null
  forceRss: SanteAccordIncludeRss
}

type SanteAccordVersionGarantieRowSave = {
  garanties: SanteAccordVersionGarantieRowSaveItem[]
}

type SanteGarantieSave = {
  valeur: string | null
  valeurUniteId: number
  commentaire: string | null
  isActive: boolean
  forceRss: SanteAccordIncludeRss
  date: string | null
}

type SanteGarantieRowSaveItem = {
  optionId: number
  present: boolean
  valeur: string | null
  valeurUniteId: number | null
  commentaire: string | null
  isActive: boolean | null
  forceRss: SanteAccordIncludeRss
  date: string | null
}

type SanteGarantieRowSave = {
  garanties: SanteGarantieRowSaveItem[]
}

export {
  CcnAccordsView,
  SANTE_BASE_OPTION_NUMERO,
  SanteAccordCopy,
  SanteAccordCreate,
  SanteAccordIncludeRss,
  SanteAccordPatch,
  SanteAccordSummary,
  SanteChangesPagination,
  SanteAccordVersionChangeReassignment,
  SanteAccordVersionChangeReassignmentResult,
  SanteAccordVersionChangeType,
  SanteAccordVersionChangeView,
  SanteAccordVersionCreate,
  SanteAccordVersionAffectedGarantieView,
  SanteAccordVersionDetail,
  SanteAccordVersionEffectiveStatus,
  SanteAccordVersionEntityType,
  SanteAccordVersionExtensionStatus,
  SanteAccordVersionGarantieRowSave,
  SanteAccordVersionGarantieUpdate,
  SanteAccordVersionGarantieView,
  SanteAccordVersionOptionCreate,
  SanteAccordVersionOptionMutationResult,
  SanteAccordVersionOptionUpdate,
  SanteAccordVersionOptionView,
  SanteAccordVersionSourceType,
  SanteAccordVersionSummary,
  SanteAccordVersionUpdate,
  SanteAccordVersioningView,
  SanteCategoryStructure,
  SanteGarantieRowSave,
  SanteGarantieSave,
  SanteGarantieView,
  SanteNomenclatureStructure,
  SanteOptionSummary,
  SantePrestationStructure,
  isSanteBaseOption,
}
